import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useConfirmEmailChange, useCurrentUser } from "@/api/hooks";

/** Lands when the user clicks the email-change confirmation link
 *  (sent to their NEW address by ``POST /me/email/request-change``).
 *
 *  Auto-consumes the token on mount and shows the result. The user
 *  must already be signed in for this to succeed — that's the
 *  second factor (an attacker who steals the link can't apply it
 *  without an active session for the same account).
 *
 *  If they're not signed in, the API returns 401 and we route them
 *  to /login with a returnTo back to this URL so they re-auth and
 *  the click "just works." */
export function ConsumeEmailChangePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const me = useCurrentUser();
  const confirm = useConfirmEmailChange();
  const [submitted, setSubmitted] = useState(false);
  const consumed = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: consume-once-on-mount guarded by `consumed` ref; including confirm.mutate would re-run on every render of the parent.
  useEffect(() => {
    if (!token || consumed.current) return;
    if (me.isLoading) return;
    if (!me.data) {
      // Not signed in — bounce to /login with return-to. After magic-
      // link sign-in completes the user can click the email link
      // again (it's still good for 30 minutes).
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    consumed.current = true;
    confirm.mutate(
      { token },
      {
        onSettled: () => setSubmitted(true),
      },
    );
  }, [token, me.isLoading, me.data]);

  if (!token) {
    return (
      <Wrapper>
        <h1 className="font-mono text-xl font-bold">Missing token</h1>
        <p className="mt-2 text-sm text-graphite-soft">
          This link doesn't have a token attached. Try requesting a fresh email change from your
          profile.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
        >
          Back to PencilOut
        </Link>
      </Wrapper>
    );
  }

  if (!submitted || confirm.isPending) {
    return (
      <Wrapper>
        <h1 className="font-mono text-xl font-bold">Confirming…</h1>
        <p className="mt-2 text-sm text-graphite-soft">One sec — applying your email change.</p>
      </Wrapper>
    );
  }

  if (confirm.error) {
    const detail = confirm.error instanceof ApiError ? confirm.error.detail : null;
    return (
      <Wrapper>
        <h1 className="font-mono text-xl font-bold">Couldn't apply change</h1>
        <p className="mt-2 text-sm text-red-pencil">
          {typeof detail === "string"
            ? detail
            : "This link can't be used. It may have expired or already been clicked."}
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
        >
          Back to PencilOut
        </Link>
      </Wrapper>
    );
  }

  // Success — confirm.data is the new CurrentUserResponse.
  const newEmail = confirm.data?.email;
  return (
    <Wrapper>
      <h1 className="font-mono text-xl font-bold">Email updated</h1>
      <p className="mt-2 text-sm text-graphite-soft">
        Your sign-in email is now{" "}
        <strong className="font-mono text-graphite">{newEmail ?? "—"}</strong>. Use that address
        next time you request a magic-link sign-in.
      </p>
      <Link
        to="/"
        className="mt-6 inline-block rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
      >
        Back to PencilOut
      </Link>
    </Wrapper>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md text-center">
        <div className="mb-6">
          <span className="brand-wordmark text-3xl">PencilOut</span>
        </div>
        {children}
      </div>
    </div>
  );
}
